import {
  buttonLabel,
  KEYBOARD_DEVICE,
  type ControllerGlyphs,
  type InputDevice,
} from './presentation';
import type { PadMap } from './remap';

/** Gameplay bindings and their help labels share one definition. */
export const KEY_BINDINGS = {
  left: [
    { code: 'KeyA', label: 'A' },
    { code: 'ArrowLeft', label: '←' },
  ],
  right: [
    { code: 'KeyD', label: 'D' },
    { code: 'ArrowRight', label: '→' },
  ],
  down: [
    { code: 'KeyS', label: 'S' },
    { code: 'ArrowDown', label: '↓' },
  ],
  jump: [
    { code: 'KeyW', label: 'W' },
    { code: 'Space', label: 'Space' },
  ],
  attack: [
    { code: 'KeyC', label: 'C' },
    { code: 'KeyK', label: 'K' },
  ],
  block: [{ code: 'KeyV', label: 'V' }],
  throw: [{ code: 'KeyF', label: 'F' }],
  pause: [
    { code: 'Escape', label: 'Esc' },
    { code: 'KeyP', label: 'P' },
  ],
} as const;

export const PAD_BINDINGS = {
  jump: [{ button: 0 }, { button: 4 }],
  attack: [{ button: 7 }, { button: 5 }, { button: 2 }],
  block: [{ button: 6 }, { button: 1 }],
  throw: [{ button: 3 }],
  pause: [{ button: 9 }],
} as const;

export function keyMatches(action: keyof typeof KEY_BINDINGS, code: string): boolean {
  return KEY_BINDINGS[action].some((binding) => binding.code === code);
}

export function keyHeld(action: keyof typeof KEY_BINDINGS, down: ReadonlySet<string>): boolean {
  return KEY_BINDINGS[action].some((binding) => down.has(binding.code));
}

export function keyHelp(action: keyof typeof KEY_BINDINGS): string {
  return KEY_BINDINGS[action].map((binding) => binding.label).join(' / ');
}

export function padHelp(
  action: keyof typeof PAD_BINDINGS,
  device: InputDevice = KEYBOARD_DEVICE,
  override: ControllerGlyphs = 'auto',
  map?: PadMap,
): string {
  const buttons = map ? [map[action]] : PAD_BINDINGS[action].map((binding) => binding.button);
  return buttons.map((button) => buttonLabel(button, device, override)).join(' / ');
}

/** Display the same custom button mapping readPad uses, while keeping menu buttons independent. */
export function gameplayHelp(
  action: keyof typeof PAD_BINDINGS,
  device: InputDevice,
  override: ControllerGlyphs = 'auto',
  map?: PadMap,
): string {
  if (device.kind === 'gamepad') return padHelp(action, device, override, map);
  if (action === 'attack') return `Left click / ${keyHelp(action)}`;
  if (action === 'block') return `Right click / ${keyHelp(action)}`;
  return keyHelp(action);
}
